import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Star, ArrowRight, Calendar, Clock } from "lucide-react";
import { SEO } from "../components/SEO";
import { ArticleRenderer, slugify } from "../components/ArticleRenderer";
import { getReviewBySlug, REVIEWS } from "../data/reviews";
import { getToolBySlug, getToolByAffiliateKey } from "../data/tools";
import { getOutboundProps, resolveOutbound } from "../config/affiliateLinks";
import { REVIEW_PAGE } from "../constants/testIds";

export default function ReviewPage() {
  const { slug } = useParams();
  const review = getReviewBySlug(slug);
  if (!review) return <Navigate to="/reviews" replace />;
  const tool = getToolBySlug(review.toolSlug);
  const toc = review.blocks.filter((b) => b.type === "h2");
  const related = REVIEWS.filter((r) => r.slug !== slug).slice(0, 3);
  const outbound = tool ? getOutboundProps(tool.affiliateKey) : null;
  const resolved = tool ? resolveOutbound(tool.affiliateKey) : null;
  const isLive = !!(resolved && tool && resolved.key === tool.affiliateKey);
  const resolvedTool = !isLive && resolved ? getToolByAffiliateKey(resolved.key) : tool;

  return (
    <>
      <SEO title={review.title} description={review.metaDescription} image={review.heroImage} />

      <section className="relative pt-36 pb-16 px-6 lg:px-10 spotlight">
        <div className="max-w-5xl mx-auto">
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-4">Review</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-tight max-w-3xl">
            {review.title}
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} className="flex flex-wrap items-center gap-5 mt-6 text-sm text-zinc-400">
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-gold text-gold" aria-hidden="true" />{tool?.rating} / 5</span>
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" aria-hidden="true" />{review.readTime}</span>
            <span className="flex items-center gap-1"><Calendar className="w-4 h-4" aria-hidden="true" />Last tested {review.lastTested}</span>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35, duration: 0.6 }} className="mt-8 rounded-sm overflow-hidden border border-white/10 aspect-[16/7]">
            <img src={review.heroImage} alt={`${tool?.name} interface`} className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24">
        <div className="max-w-5xl mx-auto grid lg:grid-cols-[1fr_260px] gap-14">
          <ArticleRenderer blocks={review.blocks} />

          <aside className="hidden lg:block">
            <div className="sticky top-24 space-y-6">
              <div className="border border-white/10 rounded-sm p-5 bg-surface/50">
                <p className="font-mono-tag text-xs uppercase tracking-wider text-zinc-500 mb-3">On This Page</p>
                <nav className="space-y-2">
                  {toc.map((b, i) => (
                    <a key={i} href={`#${slugify(b.text)}`} data-testid={REVIEW_PAGE.tocLink(i)} className="block text-sm text-zinc-400 hover:text-white transition-colors leading-snug">
                      {b.text}
                    </a>
                  ))}
                </nav>
              </div>
              <div className="border border-gold/20 rounded-sm p-5 bg-surface/50 spotlight">
                <p className="text-white font-display font-bold text-lg">{tool?.name}</p>
                <p className="text-zinc-500 text-sm mt-1">{tool?.pricing[0].price}{tool?.pricing[0].period} to start</p>
                {outbound && (
                  <a
                    href={outbound.href}
                    target={outbound.target}
                    rel={outbound.rel}
                    data-testid={REVIEW_PAGE.affiliateCta(slug)}
                    className="mt-4 w-full h-11 bg-primary hover:bg-[#5C7CFA] text-white text-sm font-medium rounded-sm flex items-center justify-center gap-2 transition-colors"
                  >
                    {isLive ? "Try Free Trial" : `See ${resolvedTool?.name}`} <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
                  </a>
                )}
                {!isLive && (
                  <Link to="/comparison" className="block text-center text-xs text-zinc-500 mt-2 hover:text-white transition-colors">Full comparison →</Link>
                )}
              </div>
            </div>
          </aside>
        </div>
      </section>

      <section className="px-6 lg:px-10 pb-24 border-t border-white/10 pt-16">
        <div className="max-w-5xl mx-auto">
          <p className="font-mono-tag text-xs uppercase tracking-[0.2em] text-gold mb-6">Related Reviews</p>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link key={r.slug} to={`/reviews/${r.slug}`} data-testid={REVIEW_PAGE.card(r.slug)} className="group border border-white/10 rounded-sm overflow-hidden hover:border-primary/50 transition-colors">
                <img src={r.heroImage} alt={r.title} className="w-full h-32 object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                <div className="p-4">
                  <p className="text-white font-medium text-sm leading-snug">{r.title.split(":")[0]}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
